import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { Input, Tag, theme, Tooltip, Icon, Text } from 'components';
import { useBreakpoint } from 'hooks';
import { AuthContext } from 'contexts';
import TourEditContext from '../TourEditContext';
import RouteListComponent from './RouteListComponent';
import TourDateSelector from './TourDateSelector';
import { TOURS_NAME_INPUT_ID } from '../constants';
import { InviteBuyer } from '../../../components';
import { TourAutoSaveStatus } from '../types';

const TourSideNav: React.FC = () => {
  const {
    tour,
    setTour,
    tourItems,
    mobileSidenavRef,
    autoSaveStatus,
    loading,
  } = useContext(TourEditContext);

  const { auth } = useContext(AuthContext);

  const [editedTourName, setEditedTourName] = useState(tour?.name);
  const savedTourNameRef = useRef<string>(tour?.name);
  useEffect(() => {
    if (autoSaveStatus === TourAutoSaveStatus.SAVED && !loading) {
      savedTourNameRef.current = tour.name;
    }
  }, [autoSaveStatus, loading]);

  useEffect(() => {
    if (!loading) setEditedTourName(tour.name);
  }, [loading]);

  const _mapSearchResultToBuyer = ({
    label,
    value,
  }: {
    label: string;
    value: string;
  }) => ({
    key: value,
    label,
    value,
  });

  const onBuyerValueChange = useCallback(buyers => {
    setTour(prev => ({
      ...prev,
      buyers: buyers.map(_mapSearchResultToBuyer),
    }));
  }, []);

  const { md } = useBreakpoint();

  const inputRef = useRef(null);

  if (!tour) return null;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setEditedTourName(value);
    setTour(prev => ({
      ...prev,
      name: value.trim() ? value : savedTourNameRef.current,
    }));
  };

  return (
    <StyledSideNavContainer>
      <StyledNameInput
        $md={md}
        value={editedTourName}
        onChange={handleNameChange}
        ref={inputRef}
        label="Tour Name"
        placeholder={`E.g. ${auth.firstName} ${auth.lastName} Tour`}
        error={editedTourName?.trim() === ''}
        {...(!md && {
          enterkeyhint: 'Done',
          onPressEnter: () => inputRef.current.blur(),
          onMouseDown: e => {
            mobileSidenavRef.current.focusFloatingPanel(inputRef);
            e.preventDefault();
          },
          onBlur: mobileSidenavRef.current.blurFloatingPanel,
        })}
        data-cy="tours-input-name"
        id={TOURS_NAME_INPUT_ID} // used to prevent unwanted scroll: TourEditPage/hooks/useFixMobile.ts
      />
      {editedTourName?.trim() === '' && <StyledTourNameError $md={md} />}
      <TourDateSelector />
      {tourItems?.[0] && (
        <StyledInviteBuyerContainer $md={md}>
          <StyledRow>
            <StyledLabel>Invite Buyers</StyledLabel>{' '}
            <StyleTag color="blue1">New</StyleTag>
            <Tooltip
              title="When a buyer is added to a tour, they will receive request and confirmation notifications for showings within the tour."
              trigger={md ? 'hover' : 'click'}
            >
              <span>
                <StyledInfoIcon type="antdInfoCircleOutlined" />
              </span>
            </Tooltip>
          </StyledRow>
          <InviteBuyer
            agent={auth}
            listing={tourItems[0]}
            value={tour.buyers}
            onValueChange={onBuyerValueChange}
          />
        </StyledInviteBuyerContainer>
      )}

      <RouteListComponent />
    </StyledSideNavContainer>
  );
};

export default TourSideNav;

const StyledSideNavContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledInviteBuyerContainer = styled.div<{ $md: boolean }>`
  margin: ${({ $md }) => ($md ? '1px 24px 0px 24px' : '16px 12px 0px 12px')};
`;

const StyledNameInput = styled(Input)<{ $md: boolean }>`
  & > span {
    padding-bottom: 6px;
  }
  margin: ${({ $md }) => ($md ? '0px 24px 0px 24px' : '6px 12px 0px 12px')};
`;

const StyledRow = styled.div`
  display: flex;
  gap: 8px;
  margin: 18px 0;
`;

const StyleTag = styled(Tag)`
  border: 1px solid ${theme.blue6};
  color: ${theme.blue6};
  border-radius: 2px;
  margin-left: 5px;
`;

const StyledLabel = styled.label`
  font-weight: 600;
`;

const StyledInfoIcon = styled(Icon)`
  & span {
    color: #000000a6;
  }
`;

const StyledTourNameError = styled(Text).attrs({
  type: 'danger',
  children: 'Please enter a unique name for your tour',
})<{ $md: boolean }>`
  font-size: 12px;
  ${({ $md }) =>
    $md ? 'margin: 0px 24px 0px 24px' : 'margin: 0px 12px 0px 12px'};
`;
